import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Stack,
  TextField,
} from '@mui/material';

import useLocale from '../../app/hooks/useLocale';
import API from '../../app/api/api';
import { ClientPublicService } from '../../app/api/ClientPublicService';
import { documentMfaStore } from '../../app/api/documentMfaStore';

const RESEND_COOLDOWN_SECONDS = 60;
const CODE_LENGTH = 6;

interface IProps {
  open: boolean;
  onSuccess: () => void;
  onCancel: () => void;
}

const DocumentMfaModal = ({ open, onSuccess, onCancel }: IProps) => {
  const { t } = useLocale();

  const [code, setCode] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isVerifying, setIsVerifying] = useState(false);
  const [isRequesting, setIsRequesting] = useState(false);
  const [cooldown, setCooldown] = useState(0);
  const [hasSentInitialCode, setHasSentInitialCode] = useState(false);
  const initialRequestRef = useRef(false);

  const requestNewCode = async () => {
    setIsRequesting(true);
    setErrorMessage(null);
    try {
      const result = await API.requestCode();
      const cooldownSeconds =
        result.cooldownSecondsRemaining && result.cooldownSecondsRemaining > 0
          ? result.cooldownSecondsRemaining
          : RESEND_COOLDOWN_SECONDS;
      setCooldown(cooldownSeconds);
      setHasSentInitialCode(true);
    } catch {
      setErrorMessage(t.DOC_MFA_REQUEST_FAILED);
    } finally {
      setIsRequesting(false);
    }
  };

  // Send the first code automatically when the modal opens.
  useEffect(() => {
    if (open && !initialRequestRef.current) {
      initialRequestRef.current = true;
      requestNewCode();
    }
    if (!open) {
      initialRequestRef.current = false;
      setCode('');
      setErrorMessage(null);
      setIsVerifying(false);
      setIsRequesting(false);
      setCooldown(0);
      setHasSentInitialCode(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  // Tick down the resend cooldown.
  useEffect(() => {
    if (cooldown <= 0) return;
    const timer = window.setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => window.clearTimeout(timer);
  }, [cooldown]);

  const handleVerify = async () => {
    if (code.length < CODE_LENGTH) {
      setErrorMessage(t.DOC_MFA_INVALID_CODE);
      return;
    }
    setIsVerifying(true);
    setErrorMessage(null);
    try {
      const result = await API.verifyCode(new ClientPublicService.VerifyDocumentDownloadCodeInputDto({ code }));
      if (result.success && result.token) {
        documentMfaStore.setToken(result.token);
        onSuccess();
        return;
      }

      switch (result.errorCode) {
        case 'expired':
          setErrorMessage(t.DOC_MFA_EXPIRED_CODE);
          break;
        case 'too_many_attempts':
          setErrorMessage(t.DOC_MFA_TOO_MANY_ATTEMPTS);
          break;
        case 'invalid':
        default:
          setErrorMessage(t.DOC_MFA_INVALID_CODE);
          break;
      }
      setCode('');
    } catch {
      setErrorMessage(t.DOC_MFA_GENERIC_ERROR);
    } finally {
      setIsVerifying(false);
    }
  };

  const handleCodeChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, CODE_LENGTH);
    setCode(digits);
    if (errorMessage) setErrorMessage(null);
  };

  return (
    <Dialog
      open={open}
      aria-labelledby="document-mfa-title"
      PaperProps={{
        sx: {
          borderRadius: '20px 0',
          maxWidth: { mobile: '100%', tablet: 420 },
        },
      }}
    >
      <DialogTitle id="document-mfa-title">{t.DOC_MFA_TITLE}</DialogTitle>
      <DialogContent sx={{ p: 1 }}>
        <DialogContentText sx={{ mb: 2 }}>{t.DOC_MFA_DESCRIPTION}</DialogContentText>

        <Box>
          <TextField
            label={t.DOC_MFA_CODE_LABEL}
            value={code}
            onChange={(e) => handleCodeChange(e.target.value)}
            fullWidth
            inputProps={{
              inputMode: 'numeric',
              autoComplete: 'one-time-code',
              maxLength: CODE_LENGTH,
              'data-testid': 'document-mfa-code-input',
              'aria-label': t.DOC_MFA_CODE_LABEL,
            }}
            disabled={isVerifying || !hasSentInitialCode}
          />
        </Box>

        {errorMessage ? (
          <Alert severity="error" sx={{ mt: 2 }} data-testid="document-mfa-error">
            {errorMessage}
          </Alert>
        ) : null}

        <Box sx={{ mt: 2 }}>
          <Button
            variant="text"
            onClick={requestNewCode}
            disabled={isRequesting || cooldown > 0}
            data-testid="document-mfa-resend"
          >
            {cooldown > 0 ? `${t.DOC_MFA_RESEND_IN} ${cooldown}s` : isRequesting ? t.DOC_MFA_SENDING : t.DOC_MFA_RESEND}
          </Button>
        </Box>
      </DialogContent>
      <DialogActions>
        <Stack flex="1" gap={2} p={2}>
          <Button onClick={onCancel} variant="outlined" data-testid="document-mfa-cancel" disabled={isVerifying}>
            {t.DOC_MFA_CANCEL}
          </Button>
          <Button
            onClick={handleVerify}
            variant="contained"
            data-testid="document-mfa-verify"
            disabled={isVerifying || code.length < CODE_LENGTH}
          >
            {t.DOC_MFA_VERIFY}
          </Button>
        </Stack>
      </DialogActions>
    </Dialog>
  );
};

export default DocumentMfaModal;
